<template>
  <div id="orders">
    <div v-if="!canViewOrders" class="container">
      <b-alert show variant="danger">
        {{ getContentItem("MissingOpenOrdersAccess") }}
      </b-alert>
    </div>
    <div v-else class="container">
      <b-row>
        <b-col xs="12" lg="3">
          <OrdersMenu />
          <div class="mt-4">
            <OrdersSearchWidgetForm />
          </div>
        </b-col>
        <b-col xs="12" lg="9">
          <h1>{{ getContentItem("ordersButton") }}</h1>
          <div class="order-filters">
            <b-row>
              <b-col xs="12" lg="4">
                <MonthSelector
                  number-of-months="13"
                  :value="currentMonthSelection"
                  @selectMonth="SetSelectedMonth"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col xs="12" lg="10">
                <AvailableDealerCodeSelector
                  :show-all-branches-option="hasAccessToAllBranches"
                  :default-dealercode="currentDealerSelection.PrimaryDealerCode"
                  @selectDealerCode="SetSelectedDealerCode"
                />
              </b-col>
              <b-col xs="12" lg="2">
                <b-button
                  id="viewButton"
                  class="btn btn-view"
                  @click="FilterButtonClick"
                >
                  {{ getContentItem('viewButton') }}
                </b-button>
              </b-col>
            </b-row>
          </div>
          <b-row>
            <b-col xs="12">
              <b-alert id="orderMessage" show variant="secondary">
                {{ message }}
              </b-alert>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <b-form-group :label-cols="3" :label="getContentItem('perPage')">
                <b-form-select
                  v-model="pagination.perPage"
                  size="sm"
                  :options="pagination.perPageList"
                  class="mt-2"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group :label-cols="3" :label="getContentItem('filter')">
                <b-form-input
                  v-model="textFilter"
                  size="sm"
                  :placeholder="getContentItem('filter')"  
                  @keyup="checkTextFilterForChanges()"   
                  @keydown="resetFilteredHelper()"          
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col xs="12">
              <b-table
                id="openOrdersTable"
                ref="ordersTableRef"
                striped
                hover
                stacked="md"
                class="blue"
                sort-by="OrderDate"
                :sort-desc="true"
                :items="SearchOrders"
                :fields="openOrderColumns"
                :no-provider-sorting="true"
                :no-provider-paging="true"
                :no-provider-filtering="true"
                :filter="textFilter"
                :filter-function="filterGrid"
                :current-page="pagination.currentPage"
                :per-page="pagination.perPage"
                show-empty
                empty-text="No Orders Found"
                :busy="isLoadingOrders"
                @sort-changed="paginationReset"
              >
                <template slot="table-busy">
                  <loading-spinner class="mx-auto" />
                </template>
                <template slot="OrderNumber" slot-scope="row">
                  <div v-if="row.item.OrderStatus === 'NotPlaced' || row.item.OrderStatus === 'Pending' || row.item.OrderStatus === 'Offline'">
                    {{ row.item.OrderNumber }}  
                  </div>
                  <router-link
                    v-else
                    :to="{ name: 'OrderDetails', query: { orderNumber: row.item.OrderNumber, orderSource: row.item.OrderSource, dealerCode: row.item.DealerCode, salesOrg: row.item.SalesOrganization }}"
                  >
                    {{ row.item.OrderNumber }}
                  </router-link>
                </template>
                <template slot="SubTotal" slot-scope="row">
                  <currency-text
                    :amount="row.item.SubTotal"
                    :currency-code="row.item.CurrencyCode"
                  />
                </template>
              </b-table>
            </b-col>
          </b-row>
          <b-row v-if="pagination.totalRows > 0">
            <b-col
              id="paginationDisplay"
              md="6"
              class="pull-left"
            >
              {{ showPagination }}
            </b-col>
            <b-col md="6">
              <b-pagination
                v-model="pagination.currentPage"
                :total-rows="pagination.totalRows"
                :per-page="pagination.perPage"
                class="pull-right"
                aria-controls="openOrdersTable"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
  import { mapGetters }                      from "vuex";
  import moment                              from "moment";
  import { OrderService }                    from "@/services/OrderService";
  import { ErrorLogService }                 from "@/services/ErrorLogService";
  import { DealerUtil }                      from "@/services/";
  import { FeatureFlagService }             from "@/services/FeatureFlagService"
  import OrdersMenu                          from "@/components/SubMenus/OrdersMenu";
  import OrdersSearchWidgetForm              from "@/components/SubMenus/OrdersSearchWidgetForm";
  import CurrencyText                        from "@/components/Elements/CurrencyText";
  import { formatDateTime }                  from "@/components/mixins/formatDateTime";
  import { paginationMixin }                 from "@/components/mixins/paginationMixin";
  import { stringHelper }                    from "@/components/mixins/stringHelper";
  import MonthSelector                       from "@/components/Dropdowns/MonthSelector";
  import AvailableDealerCodeSelector         from "@/components/Dropdowns/AvailableDealerCodeSelector";
  import { LoadingSpinner }                  from "@/components";
  import { constants }                      from "@/const";


  export default {
    name: "Orders",
    components: {
      OrdersMenu,
      OrdersSearchWidgetForm,
      CurrencyText,
      MonthSelector,
      AvailableDealerCodeSelector,
      LoadingSpinner
    },
    mixins: [formatDateTime, paginationMixin, stringHelper],
    data: () => ({
      allOpenOrderColumns: [
        { key: "OrderNumber", labelKey: "orderNumber", sortable: true },
        { key: "WebOrigin", labelKey: "origin", sortable: true },
        { key: "PurchaseOrderNumber", labelKey: "poNumber", sortable: true },
        { key: "OrderDate", labelKey: "date", sortable: true, formatter: "formatDateTime" },
        { key: "OrderStatus", labelKey: "status", sortable: true },
        { key: "ShipmentType", labelKey: "orderType", sortable: true },
        { key: "SubTotal", labelKey: "total", sortable: true }
      ],
      defaultSearchParams: {},
      textFilter: null,
      prevTextFilter: null,
      allPrimaryDealerCodes: null,
      stagedDealerSelection: null,
      currentDealerSelection: null,
      stagedMonthSelection: null,
      currentMonthSelection: null,
      sortDesc: true,
      isLoadingOrders: false,
      rowsFiltered: false,
      rowsFilteredHelper: false,
    }),
    watch: {
      "currentMonthSelection" () {
        this.SetRecentComponentData();
      },
      "stagedDealerSelection" () {
        this.SetRecentComponentData();
      },

    },
    computed: {
      ...mapGetters([
        "user",
        "getContentItem",
        "hasPermission",
        "canSeeAnyPrice",
        "availableDealerCodes",
        "activeAccountId",
        "getRecentComponentName",
        "getRecentComponentData"
      ]),
      showPagination(){     
        if(this.rowsFiltered) {
          return this.format(this.getContentItem("showingFilteredPagination"));  
        }
        else{
          return this.format(this.getContentItem("showingPagination"), this.paginationFirstRecord, this.paginationLastRecord, this.pagination.totalRows);  
        }
      },
      canViewOrders() {
        return this.hasPermission("CanSeeOrders");
      },
      canViewPrice() {
        return (
          this.canSeeAnyPrice &&
          !this.hasPermission("HideInvoicePricing")
        );
      },
      hasAccessToAllBranches() {
        return (
          this.availableDealerCodes.length > 1 && this.user.DealerRange !== "*"
        );
      },
      openOrderColumns() {
        if (this.canViewPrice) {
          return this.allOpenOrderColumns;
        }
        return this.allOpenOrderColumns.filter(
          column => column.key !== "SubTotal"
        );
      },
      dealerFilter() {
        if (
          this.currentDealerSelection.hasOwnProperty("value") &&
          this.currentDealerSelection.value === "All Branches"
        ) {
          return this.allPrimaryDealerCodes;
        } else {
          return this.currentDealerSelection.DealerCodes.replace(/\s/g, "").split(
            ","
          );
        }
      },
      monthFilter() {
        //Need Date format parameter for IE11
        //See more info here: https://momentjs.com/docs/#/parsing/string/
        return moment(this.currentMonthSelection,"MMMM YYYY");
      },
      message() {
        let displayWithSubDealers = ""
        if (this.currentDealerSelection !== undefined )
        {
          displayWithSubDealers = this.currentDealerSelection.DisplayWithSubDealers
        }
        return this.format(
          this.getContentItem("orderViewMessage"),
          displayWithSubDealers,
          this.currentMonthSelection
        );
      },
    },
    created() {
      // availableDealerCodes is a bit of a misnomer as each element has more data than just a DealerCode
      this.allPrimaryDealerCodes = this.availableDealerCodes.map(
        dealer => dealer.PrimaryDealerCode
      );

      // Match's web/src/components/Dropdowns/AvailableDealerCodeSelector.vue setup
      this.currentDealerSelection = DealerUtil.determineCurrentDealerSelection(
        this.availableDealerCodes,
        this.user,
        this.hasAccessToAllBranches
      );
      if(this.getRecentComponentName === "Orders"){
        this.currentMonthSelection = this.getRecentComponentData.currentMonthSelection
        this.stagedDealerSelection = this.getRecentComponentData.stagedDealerSelection
        if(this.IsNotNullOrUndefined(this,"getRecentComponentData.stagedDealerSelection.PrimaryDealerCode")){
          this.currentDealerSelection = this.availableDealerCodes.find(
            dealer => dealer.PrimaryDealerCode === this.getRecentComponentData.stagedDealerSelection.PrimaryDealerCode
          );
        }  
      }else{
        this.currentMonthSelection = moment().format("MMMM YYYY");
      }
      
      this.allOpenOrderColumns = this.allOpenOrderColumns.map(column =>
        Object.assign({}, column, { label: this.getContentItem(column.labelKey) })
      );
    },
    methods: {
      SearchOrders: function() {
        this.isLoadingOrders = true
        var dealerCodesToSearch = this.dealerFilter;
        if(dealerCodesToSearch.length > 100)
        {
          dealerCodesToSearch = [this.user.DealerCode];
        }

        let searchParams = {
          AccountId: this.activeAccountId,
          UserId: this.user.UserId,
          StartDate: this.monthFilter.startOf("month").format("MM/DD/YYYY"),
          EndDate: this.monthFilter.endOf("month").format("MM/DD/YYYY"),
          DealerCodes: dealerCodesToSearch,
          IncludeOfflineOrders: FeatureFlagService.getFeatureEnabled(constants.FEATURE_NAMES.OFFLINE_ORDER_PROCESSING)
        };

        return OrderService.GetRecentOrderActivity(searchParams)
          .then(results => {
            this.pagination.totalRows = results.data.length;
            if (this.canViewPrice) {
              return results.data;
            }
            return results.data.map(record => {
              delete record.SubTotal;
              return record;
            });
          })
          .catch(err => {
            ErrorLogService.LogException(err);
            return [];
          })
          .finally(() => {
            this.isLoadingOrders = false
          })
      },
      SetSelectedDealerCode: function(selectedDealer) {
        this.stagedDealerSelection = selectedDealer;
      },
      SetSelectedMonth: function(selectedMonth) {
        this.stagedMonthSelection = selectedMonth;
      },
      FilterButtonClick: function() {
        if (this.stagedDealerSelection) {
          this.currentDealerSelection = this.stagedDealerSelection;
        }
        if (this.stagedMonthSelection) {
          this.currentMonthSelection = this.stagedMonthSelection;
        }

        if (this.$refs.ordersTableRef.refresh) {
          this.$refs.ordersTableRef.refresh();
        }
      },
      SetRecentComponentData(){
        var componentData = {};
        componentData.currentMonthSelection = this.currentMonthSelection;
        componentData.stagedDealerSelection = this.stagedDealerSelection;
        this.$store.commit("SET_RECENT_COMPONENT", {name: "Orders", data: componentData});
      },
      filterGrid (row) {
        for (let rowCellKey in row) { 
          // skip subtotal in search
          if (rowCellKey == "SubTotal") {
            continue
          }
          
          let rowCellValue = row[rowCellKey] 
          if (!rowCellValue)
          {
            continue
          }
          
          //Text match check
          if(rowCellValue.toLowerCase().includes(this.textFilter.toLowerCase())){
            return true;
          }
          //Date match check
          let rowCellValueAsDate = this.formatDateTime(rowCellValue);
          if(rowCellValueAsDate !== ""){  
            let rowCellValueAsDateWithoutSpecialChars = rowCellValueAsDate
            let textFilterWithoutSpecialChars = this.textFilter;
            let specialCharsRegex = /\/|\.|\\|\-/gi  
            rowCellValueAsDateWithoutSpecialChars = rowCellValueAsDateWithoutSpecialChars.replace(specialCharsRegex ,"");
            textFilterWithoutSpecialChars = textFilterWithoutSpecialChars.replace(specialCharsRegex ,"");
            if(rowCellValueAsDateWithoutSpecialChars.includes(textFilterWithoutSpecialChars)){
              return true;
            }
          }   
        }
        this.rowsFilteredHelper = true;
        return false
      },
 
      //keyup action event for textbox filter
      checkTextFilterForChanges(){
        if(this.textFilter !== this.prevTextFilter){        
          this.rowsFiltered = this.rowsFilteredHelper;
        }
        this.prevTextFilter = this.textFilter;
      },
      //keydown action event for textbox filter
      resetFilteredHelper(){
        this.rowsFilteredHelper = false;
      }
    }
  };
</script>
